import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabase } from "../../lib/supabase/client";

type Stage = "checking" | "form" | "saving" | "done" | "error";

function parseHashParams(hash: string) {
  const out: Record<string, string> = {};
  const h = hash.startsWith("#") ? hash.slice(1) : hash;
  for (const pair of h.split("&")) {
    if (!pair) continue;
    const [k, v] = pair.split("=");
    if (k) out[decodeURIComponent(k)] = decodeURIComponent(v ?? "");
  }
  return out;
}

export default function ResetPasswordPage() {
  const supabase = getSupabase();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>("checking");
  const [error, setError] = useState<string | null>(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");

  const fullUrl = useMemo(() => window.location.href, []);
  const search = useMemo(() => new URLSearchParams(window.location.search), []);
  const hashParams = useMemo(() => parseHashParams(window.location.hash), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setStage("checking");
      setError(null);

      try {
        // 1) If we already have a session, just show the form
        const first = await supabase.auth.getSession();
        if (first.data.session?.user) {
          if (!cancelled) setStage("form");
          return;
        }

        // 2) Try all supported exchange paths

        // 2a) PKCE code flow (?code=...) -> exchangeCodeForSession
        const code = search.get("code");
        if (code) {
          try {
            const { data, error } = await supabase.auth.exchangeCodeForSession(fullUrl);
            if (error) {
              console.warn("[/auth/reset] exchangeCodeForSession error:", error);
            } else {
              console.log("[/auth/reset] PKCE exchange success:", {
                hasSession: !!data.session,
                userId: data.session?.user?.id,
              });
            }
          } catch (e) {
            console.warn("[/auth/reset] exchangeCodeForSession threw:", e);
          }
        }

        // 2b) Hash tokens (#access_token=&refresh_token=) -> setSession
        if (hashParams.access_token && hashParams.refresh_token) {
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: hashParams.access_token,
              refresh_token: hashParams.refresh_token,
            });
            if (error) {
              console.warn("[/auth/reset] setSession error:", error);
            } else {
              console.log("[/auth/reset] setSession success:", {
                hasSession: !!data.session,
                userId: data.session?.user?.id,
              });
            }
          } catch (e) {
            console.warn("[/auth/reset] setSession threw:", e);
          }
        }

        // 2c) Email OTP (?type=recovery&token_hash=...&email=...)
        const type = (search.get("type") || "").toLowerCase();
        const token_hash = search.get("token_hash");
        const email = search.get("email");
        if ((type === "recovery" || type === "magiclink") && token_hash && email) {
          try {
            const { data, error } = await supabase.auth.verifyOtp({
              type: "email",
              token_hash,
              email,
            });
            if (error) {
              console.warn("[/auth/reset] verifyOtp error:", error);
            } else {
              console.log("[/auth/reset] verifyOtp success:", {
                hasSession: !!data.session,
                userId: data.session?.user?.id,
              });
            }
          } catch (e) {
            console.warn("[/auth/reset] verifyOtp threw:", e);
          }
        }

        // 3) Final session check
        const { data } = await supabase.auth.getSession();
        const hasSession = !!data.session?.user;
     

        if (cancelled) return;

        if (hasSession) {
          setStage("form");
        } else {
          setStage("error");
          setError(
            "Reset link is invalid or expired. Request a new link from the Sign In page."
          );
        }
      } catch (e: any) {
        if (cancelled) return;
        console.error("[/auth/reset] unexpected error:", e);
        setStage("error");
        setError(e?.message ?? "Unexpected error while preparing password reset.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [supabase, fullUrl, search, hashParams]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!pwd || pwd.length < 8) return setError("Password must be at least 8 characters.");
    if (pwd !== pwd2) return setError("Passwords do not match.");

    setStage("saving");
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) throw error;

      setStage("done");
      setTimeout(() => navigate("/dashboard", { replace: true }), 800);
    } catch (e: any) {
      console.error("[/auth/reset] updateUser error:", e);
      setStage("form");
      setError(e?.message ?? "Could not set the new password. Try again.");
    }
  }

  if (stage === "checking") {
    return (
      <div className="max-w-md mx-auto p-6">
        <h1 className="text-2xl font-semibold mb-2">Resetting password…</h1>
        <p className="text-sm text-gray-600">Preparing your reset session.</p>
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div className="max-w-md mx-auto p-6">
        <h1 className="text-2xl font-semibold mb-2">Password reset error</h1>
        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
        <a className="underline" href="/signin">Back to Sign In</a>
      </div>
    );
  }

  if (stage === "done") {
    return (
      <div className="max-w-md mx-auto p-6">
        <h1 className="text-2xl font-semibold mb-2">Password updated</h1>
        <p className="text-sm text-gray-700">Redirecting you to your dashboard…</p>
      </div>
    );
  }

  // stage: "form" | "saving"
  return (
    <div className="max-w-md mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-2">Set a new password</h1>
      <p className="text-sm text-gray-600 mb-4">
        You’re signed in from a secure reset link. Choose a new password below.
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium">New password</label>
          <input
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="w-full border rounded px-3 py-2"
            placeholder="••••••••"
            autoComplete="new-password"
            minLength={8}
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Repeat new password</label>
          <input
            type="password"
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            className="w-full border rounded px-3 py-2"
            placeholder="••••••••"
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        <button
          type="submit"
          disabled={stage === "saving"}
          className="w-full rounded bg-black text-white py-2"
        >
          {stage === "saving" ? "Saving…" : "Save new password"}
        </button>
      </form>

      {error && <p className="mt-3 text-red-600 text-sm">{error}</p>}

      <div className="mt-4 text-sm">
        <a className="underline" href="/auth/debug">Auth debug</a>
      </div>
    </div>
  );
}
